/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import usePluginUtils from "./usePluginUtils";
import { useClientTerms } from "../lib/react-query/useClientTerms";
import { useConfig } from "../lib/react-query/useConfig";
import { CLIENT_REGISTER_POLICY_FIELDS_KEYS } from "../constants/constants";

const REQUIRED_TERM_KEYS = [
  CLIENT_REGISTER_POLICY_FIELDS_KEYS.SIMPLYBOOK_TERMS,
  CLIENT_REGISTER_POLICY_FIELDS_KEYS.CANCELLATION_TERMS,
  CLIENT_REGISTER_POLICY_FIELDS_KEYS.USER_TERMS,
  CLIENT_REGISTER_POLICY_FIELDS_KEYS.PRIVACY_POLICY,
];

const TERMS_SENTINEL = ["__no_required_terms__"];
const GUEST_SENTINEL = ["__no_required_guest_fields__"];

export const useRequiredFieldsCompletion = ({ form }) => {
  const { control } = form;
  const { isPluginEnabled } = usePluginUtils();
  const { data: terms } = useClientTerms();
  const { data: config } = useConfig();

  const isLoading = terms === undefined || config === undefined;
  const showGuestFields = !isPluginEnabled("client_login");

  const pendingRequiredTermKeys = useMemo(() => {
    if (!terms) return [];

    return REQUIRED_TERM_KEYS.filter((key) => terms[`enabled_${key}`] && !terms[`accepted_${key}`]);
  }, [terms]);

  const watchedTermValues = useWatch({
    control,
    name: pendingRequiredTermKeys.length > 0 ? pendingRequiredTermKeys : TERMS_SENTINEL,
  });

  const allRequiredTermsAccepted = useMemo(() => {
    if (pendingRequiredTermKeys.length === 0) return true;
    if (!Array.isArray(watchedTermValues)) return false;

    return watchedTermValues.every(Boolean);
  }, [pendingRequiredTermKeys, watchedTermValues]);

  const requiredGuestFieldNames = useMemo(() => {
    if (!showGuestFields) return [];

    const names = ["name"];

    if (config?.client_email_required) names.push("email");
    if (config?.client_phone_required) names.push("phone");

    return names;
  }, [showGuestFields, config]);

  const watchedGuestFieldValues = useWatch({
    control,
    name: requiredGuestFieldNames.length > 0 ? requiredGuestFieldNames : GUEST_SENTINEL,
  });

  const allRequiredGuestFieldsFilled = useMemo(() => {
    if (requiredGuestFieldNames.length === 0) return true;
    if (!Array.isArray(watchedGuestFieldValues)) return false;

    return watchedGuestFieldValues.every((value) =>
      typeof value === "string" ? value.trim().length > 0 : Boolean(value)
    );
  }, [requiredGuestFieldNames, watchedGuestFieldValues]);

  return {
    allRequiredTermsAccepted,
    allRequiredGuestFieldsFilled,
    isComplete: !isLoading && allRequiredTermsAccepted && allRequiredGuestFieldsFilled,
  };
};
